// src/stores/audioStore.js
import { defineStore } from 'pinia'

const AUDIO_BASE = 'https://joygma.online/audio'
const MAP_BGM_DIR = 'map' // 地图BGM目录：/audio/map/<mapId>.mp3

const battleBGMList = {
  wild:    ['1.mp3', '2.mp3', '3.mp3', '4.mp3'],
  trainer: ['1.mp3', '2.mp3', '3.mp3', '4.mp3', '5.mp3', '6.mp3', '7.mp3', '8.mp3', '9.mp3', '10.mp3'],
  boss:    ['1.mp3', '2.mp3', '3.mp3', '4.mp3'],
}

export const useAudioStore = defineStore('audio', {
  state: () => ({
    bgmAudio: null,           // 背景音乐音轨对象
    currentBGM: '',           // 当前播放的 BGM URL（或标识）
    isMuted: false,           // 是否静音
    bgmVolume: 0.5,           // 背景音乐音量（0 ~ 1）
    sfxVolume: 0.8,           // 音效音量（0 ~ 1）
    sfxPool: {},              // 可选：缓存常用音效，避免重复创建 Audio
  }),

  actions: {
    /* ================== 新增：按地图ID播放BGM ================== */
    playMapBGM(mapId) {
      if (this.isMuted) return
      const id = String(mapId ?? '').trim()
      if (!id) return

      const url = `${AUDIO_BASE}/${MAP_BGM_DIR}/${id}.mp3`
      if (this.currentBGM === url && this.bgmAudio) return

      // 停止旧BGM
      if (this.bgmAudio) {
        try { this.bgmAudio.pause() } catch {}
        this.bgmAudio = null
      }

      const audio = new Audio(url)
      audio.loop = true
      audio.volume = this.bgmVolume
      audio.play().catch(() => {}) // 某些浏览器需用户交互
      this.bgmAudio = audio
      this.currentBGM = url
    },

    /* ================== 宝可梦叫声 ================== */
    playPokemonCry(num) {
      if (this.isMuted) return

      const tryPlay = (id) => {
        const url = `${AUDIO_BASE}/pokemon/cries/${id}.mp3`
        const audio = new Audio(url)
        audio.volume = this.sfxVolume
        audio.play().catch(() => {})
        audio.onerror = () => {
          const randomId = Math.floor(Math.random() * 1025) + 1
          if (randomId !== id) {
            const randomUrl = `${AUDIO_BASE}/pokemon/cries/${randomId}.mp3`
            const randomAudio = new Audio(randomUrl)
            randomAudio.volume = this.sfxVolume
            randomAudio.play().catch(() => {})
          }
        }
      }

      tryPlay(num)
    },

    /* ================== 战斗BGM（随机） ================== */
    playRandomBattleBGM(type) {
      if (this.isMuted) return

      const list = battleBGMList[type]
      if (!list || !list.length) return

      const name = list[Math.floor(Math.random() * list.length)]
      const url = `${AUDIO_BASE}/battle/${type}/${name}`

      if (this.bgmAudio) {
        try { this.bgmAudio.pause() } catch {}
        this.bgmAudio = null
      }
      const audio = new Audio(url)
      audio.loop = true
      audio.volume = this.bgmVolume
      audio.play().catch(() => {})
      this.bgmAudio = audio
      this.currentBGM = url
    },

    /* ================== 通用路径播放（保留） ================== */
    playBGMWithPath(path, name) {
      if (this.isMuted) return

      const url = `${AUDIO_BASE}/${String(path).replace(/\/+$/,'')}/${name}.mp3`
      if (this.currentBGM === url && this.bgmAudio) return

      if (this.bgmAudio) {
        try { this.bgmAudio.pause() } catch {}
        this.bgmAudio = null
      }

      const audio = new Audio(url)
      audio.loop = true
      audio.volume = this.bgmVolume
      audio.play().catch(() => {})
      this.bgmAudio = audio
      this.currentBGM = url
    },

    /* ================== 旧版按名播放（保留兼容） ================== */
    playBGM(name) {
      if (this.isMuted) return

      const url = `${AUDIO_BASE}/bg/${name}.mp3`
      if (this.currentBGM === url && this.bgmAudio) {
        return
      }

      if (this.bgmAudio) {
        try { this.bgmAudio.pause() } catch {}
        this.bgmAudio = null
      }

      const audio = new Audio(url)
      audio.loop = true
      audio.volume = this.bgmVolume
      audio.play().catch(() => {})
      this.bgmAudio = audio
      this.currentBGM = url
    },

    // 停止背景音乐
    stopBGM() {
      if (this.bgmAudio) {
        try { this.bgmAudio.pause() } catch {}
        this.bgmAudio = null
        this.currentBGM = ''
      }
    },

    // 播放音效（支持重复触发）
    playSFX(name) {
      if (this.isMuted) return

      const url = `${AUDIO_BASE}/main/${name}.mp3`
      const audio = new Audio(url)
      audio.volume = this.sfxVolume
      audio.play().catch(() => {})
    },

    // 播放缓存的音效（减少重复加载）
    playSFXCached(name) {
      if (this.isMuted) return

      if (!this.sfxPool[name]) {
        const audio = new Audio(`${AUDIO_BASE}/main/${name}.mp3`)
        audio.volume = this.sfxVolume
        this.sfxPool[name] = audio
      }

      const audio = this.sfxPool[name].cloneNode() // 克隆才能重复播放
      audio.volume = this.sfxVolume
      audio.play().catch(() => {})
    },

    // 切换静音状态
    toggleMute() {
      this.isMuted = !this.isMuted
      if (this.bgmAudio) {
        if (this.isMuted) this.bgmAudio.pause()
        else this.bgmAudio.play().catch(() => {})
      }
    },

    // 设置音量
    setVolume(type, value) {
      if (type === 'bgm') {
        this.bgmVolume = value
        if (this.bgmAudio) this.bgmAudio.volume = value
      } else if (type === 'sfx') {
        this.sfxVolume = value
      }
    },
  },
})
